import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  filterStorageKey,
  matchesFilters,
  pruneValues,
  purgeLegacyFilterKeys,
  readStoredFilters,
  writeStoredFilters,
  type FilterGroups,
  type FilterValues,
} from '@/lib/table-filters'
import { useAuthStore } from '@/stores/auth-store'

export interface ActiveChip {
  key: string
  value: string
  /** Option label, e.g. "Critical". */
  label: string
  /** Group label, e.g. "Severity". */
  groupLabel: string
}

interface UseTableFiltersOptions<T> {
  bidId: string
  /** Storage namespace, e.g. 'compliance_matrix'. */
  tableKey: string
  groups: FilterGroups<T>
  /**
   * Map URL params onto filter values. Return `null` when the URL carries no
   * filter intent, so stored state is used instead. Returning an object makes
   * the URL win outright — a link should show what its sender meant.
   */
  urlSeed?: (params: URLSearchParams) => Record<string, string[]> | null
  /**
   * The param names `urlSeed` reads, stripped from the address bar once applied.
   *
   * A nudge link is a view to land on, not a preference to adopt, and leaving
   * the params in place makes it neither. The mount-time seed re-wins every time
   * the table remounts — which a sub-tab switch does on every return — so
   * `Clear all` cannot stick and only an address bar edit undoes the filter.
   * Consuming the param is what makes the seeded view dismissable.
   *
   * Must be a stable reference: it sits in an effect dep array.
   */
  consumeParams?: readonly string[]
}

export interface UseTableFiltersResult<T> {
  values: FilterValues
  toggle: (key: string, value: string) => void
  setMany: (patch: Record<string, string[]>) => void
  clear: (key: string) => void
  clearAll: () => void
  activeChips: ActiveChip[]
  matches: (item: T) => boolean
  hasAny: boolean
}

function toValues(patch: Record<string, string[]>): FilterValues {
  const out: FilterValues = {}
  for (const [key, list] of Object.entries(patch)) {
    if (list.length > 0) out[key] = new Set(list)
  }
  return out
}

export function useTableFilters<T>({
  bidId,
  tableKey,
  groups,
  urlSeed,
  consumeParams,
}: UseTableFiltersOptions<T>): UseTableFiltersResult<T> {
  const [searchParams, setSearchParams] = useSearchParams()
  // Per-user, not just per-bid: a shared browser used to hand the next account
  // the previous one's hidden-row state. See `filterStorageKey`.
  const userId = useAuthStore((s) => s.user?.id)
  const storageKey = filterStorageKey(userId, bidId, tableKey)

  // The unscoped keys the old format left behind are unreadable now, so clear
  // them out once rather than leaving one account's state on the machine.
  useEffect(() => {
    purgeLegacyFilterKeys()
  }, [])

  // `groups` is rebuilt every render by callers (option lists are memo'd
  // derivations), so it must not sit in an effect dep array.
  const groupsRef = useRef(groups)
  groupsRef.current = groups

  /** The URL's filter intent, with the signature the seeding effect dedupes on. */
  const readSeed = (): { values: FilterValues; signature: string } | null => {
    const seeded = urlSeed?.(searchParams)
    if (!seeded) return null
    return {
      values: pruneValues(toValues(seeded), groupsRef.current),
      signature: JSON.stringify(seeded),
    }
  }

  // URL intent beats stored state; stored state is the fallback.
  const initialValues = (): FilterValues =>
    readSeed()?.values ?? readStoredFilters(storageKey, groupsRef.current)

  const [values, setValues] = useState<FilterValues>(initialValues)

  // Re-seed whenever the URL changes, not only on mount — next-best-action
  // links navigate within the tab without remounting this component.
  const seededRef = useRef<string | null>(null)

  // A URL-derived view is not the user's saved preference, so it is held back
  // from storage until they change something themselves. Without this, merely
  // ARRIVING on a "3 mandatory at risk" nudge rewrote the bid's default matrix
  // view — the seed reached the persistence effect on the very first commit,
  // before the user had touched a control.
  const skipPersistRef = useRef(false)

  // Switching bids must reset the filters. React Router reuses the element
  // across `:bidId` changes, so this hook stays mounted when the user
  // navigates bid A → bid B — without this, A's filters would be applied to
  // B's rows and then written over B's stored filters. Adjusting state during
  // render (rather than in an effect) keeps the stale values from ever
  // reaching the persistence effect.
  //
  // The previous key is held in state, not a ref: Router 7 navigates inside
  // `startTransition`, and a transition render can be discarded and restarted.
  // A ref is shared with the work-in-progress fiber, so a ref write would
  // survive a discarded render while the `setValues` beside it was thrown
  // away — skipping the reset on the restart. State is discarded with it.
  const [prevStorageKey, setPrevStorageKey] = useState(storageKey)
  if (prevStorageKey !== storageKey) {
    setPrevStorageKey(storageKey)
    const seed = readSeed()
    // Record the signature rather than clearing it. Clearing arms a delayed
    // re-seed: the effect does not run on a bid switch (its deps are
    // `searchParams`, memoised on `location.search`, and `urlSeed`), so a
    // later unrelated search-param change would re-seed from the stale URL
    // and wipe whatever the user had selected since the switch.
    seededRef.current = seed?.signature ?? null
    skipPersistRef.current = seed !== null
    setValues(seed?.values ?? readStoredFilters(storageKey, groupsRef.current))
  }

  useEffect(() => {
    const seeded = urlSeed?.(searchParams) ?? null
    if (seeded) {
      const signature = JSON.stringify(seeded)
      if (seededRef.current !== signature) {
        seededRef.current = signature
        skipPersistRef.current = true
        setValues(pruneValues(toValues(seeded), groupsRef.current))
      }
    }

    // Consume the params AFTER seeding, and unconditionally — a param the seed
    // rejected as unparseable is still spent, or it would keep re-arming.
    // Rewriting the query string re-runs this effect with the params gone, so
    // `changed` is false the second time and it settles in one extra pass.
    if (!consumeParams?.length) return
    const next = new URLSearchParams(searchParams)
    let changed = false
    for (const key of consumeParams) {
      if (next.has(key)) {
        next.delete(key)
        changed = true
      }
    }
    if (changed) setSearchParams(next, { replace: true })
  }, [searchParams, urlSeed, consumeParams, setSearchParams])

  useEffect(() => {
    // See `skipPersistRef`: a view the user was SENT to is not one they chose.
    if (skipPersistRef.current) return
    writeStoredFilters(storageKey, values)
  }, [storageKey, values])

  /**
   * Every mutator lifts the persistence hold: changing anything by hand is the
   * point at which the view stops being the link's and starts being the user's.
   * That is also what lets `Clear all` stick — it writes the empty set over the
   * seeded values instead of being skipped.
   */
  const beginUserEdit = () => {
    skipPersistRef.current = false
  }

  const toggle = useCallback((key: string, value: string) => {
    beginUserEdit()
    setValues((prev) => {
      const next = new Set(prev[key] ?? [])
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return { ...prev, [key]: next }
    })
  }, [])

  /**
   * Replace the listed groups outright. Unlike `toValues`, an empty list here
   * means "clear this group" rather than "no change" — the compliance at-risk
   * chip relies on that to toggle itself off.
   */
  const setMany = useCallback((patch: Record<string, string[]>) => {
    beginUserEdit()
    setValues((prev) => {
      const next = { ...prev }
      for (const [key, list] of Object.entries(patch)) next[key] = new Set(list)
      return next
    })
  }, [])

  const clear = useCallback((key: string) => {
    beginUserEdit()
    setValues((prev) => ({ ...prev, [key]: new Set() }))
  }, [])

  const clearAll = useCallback(() => {
    beginUserEdit()
    setValues({})
  }, [])

  const activeChips = useMemo(() => {
    const chips: ActiveChip[] = []
    for (const [key, config] of Object.entries(groups)) {
      for (const value of values[key] ?? []) {
        chips.push({
          key,
          value,
          // `missingLabel` before the raw value: an unnameable selection is a
          // real state on data-derived groups, and the stored value is a UUID.
          label:
            config.options.find((o) => o.value === value)?.label ??
            config.missingLabel ??
            value,
          groupLabel: config.label,
        })
      }
    }
    return chips
  }, [groups, values])

  const matches = useCallback(
    (item: T) => matchesFilters(item, groupsRef.current, values),
    [values]
  )

  const hasAny = activeChips.length > 0

  return { values, toggle, setMany, clear, clearAll, activeChips, matches, hasAny }
}
